import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  HostListener,
  NgZone,
  OnInit,
} from '@angular/core';

import { App } from '@capacitor/app'; // ✅ at top of file

import { defineCustomElement as defineIonModal } from '@ionic/core/components/ion-modal';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { nativeSocialLoginConfig } from '@app/config/google-oauth';
import { NavController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Platform, ToastController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions'; // Import the module
import { NgxPermissionsService } from 'ngx-permissions'; // Import the Permissions Service
import { IonicModule } from '@ionic/angular';
import { registerLocaleData } from '@angular/common';

import {
  ModalController,
  ActionSheetController,
  AlertController,
  PopoverController,
} from '@ionic/angular';
import { PushNotificationService } from './services/push-notification.service';
import { DarkModeService } from './services/dark-mode.service';
import { StatusBar } from '@capacitor/status-bar';
// import { shouldSkipAutoRedirects, isDevMode } from './config/app-config';
// import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

import { inject } from '@angular/core';
import { LanguageService } from './services/language.service';
import { AuthService } from './services/auth.service';
import { tr } from 'intl-tel-input/i18n';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeAr from '@angular/common/locales/ar';
import { UserService } from './services/rest.service';
import { AppUpdateService } from './services/app-update.service';
import { AppLinkService } from './services/app-link.service';
import { openUrlInAndroidChrome } from './services/android-chrome';
registerLocaleData(localeEn, 'en');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeAr, 'ar');

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    FormsModule,
    HttpClientModule,

    NgxPermissionsModule, // Add this to the imports of the standalone component
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [NgxPermissionsService, Calendar],
})
export class AppComponent implements OnInit {
  isweb = Capacitor.getPlatform();
  // put this near the top of the class
  deepLinkOpened = false;

  currentUser: any = null; // 👈 Declare the property

  userName: string = 'Guest';
  userEmail: string = 'guest@example.com';
  showTabBar = true;
  showHeader = true;
  rootSwipeEnabled = true;
  isMenuActive = false;
  viewportHeight: number = window.innerHeight;
  userRoles: string[] = []; // Store the roles of the user
  activeTab: string = ''; // Track which tab is active
  private escapeListener!: (e: KeyboardEvent) => void;
  private overlayHistoryDepth = 0;
  private dismissingOverlayFromHistory = false;
  private suppressNextOverlayPopstate = false;
  private readonly publicAuthRoutes = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
    '/verify-otp',
    '/verify-register-email',
  ];
  private readonly lastActiveRouteKey = 'lastActiveRoute';
  // Development mode flag - imported from config
  // private readonly DEV_MODE = isDevMode();

  constructor(
    private router: Router,
    private translate: TranslateService,
    private platform: Platform,
    private permissionsService: NgxPermissionsService,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController,
    public pushService: PushNotificationService,
    private darkModeService: DarkModeService,
    private actionSheetCtrl: ActionSheetController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    public authService: AuthService,
    public userService: UserService,
    public appUpdateService: AppUpdateService,
    private languageService: LanguageService,
    private navCtrl: NavController,
    private ngZone: NgZone,
    private appLink: AppLinkService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Detect current route and set active tab
        this.rememberActiveRoute(event.urlAfterRedirects);
        this.rootSwipeEnabled = !this.isProfileRoute(event.urlAfterRedirects);

        if (event.urlAfterRedirects.includes('/profile')) {
          this.activeTab = 'profile';
        } else if (event.urlAfterRedirects.includes('/rides')) {
          this.activeTab = 'rides';
        } else if (
          event.urlAfterRedirects.includes('/home') ||
          event.urlAfterRedirects.includes('/find-ride') ||
          event.urlAfterRedirects.includes('/add-offered-trip')
        ) {
          this.activeTab = 'home';
        } else if (event.urlAfterRedirects.includes('/admin')) {
          this.activeTab = 'admin';
        } else if (event.urlAfterRedirects.includes('/notifications')) {
          this.activeTab = 'notifications';
        } else {
          this.activeTab = ''; // Default
        }
      }
    });

    defineIonModal();
    const supportedLangs = ['en', 'fr', 'es', 'ar'];
    const savedLang = languageService.currentLang;

    let lang: string;

    if (savedLang && supportedLangs.includes(savedLang)) {
      lang = savedLang; // ✅ user-selected
    } else {
      const browserLang =
        navigator.language || this.translate.getBrowserLang() || 'en';
      const langCode = browserLang.slice(0, 2);
      lang = supportedLangs.includes(langCode) ? langCode : 'en';
    }

    // ✅ Set the language
    this.translate.setDefaultLang(lang);
    this.translate.use(lang); // ✅ ADD THIS

    // ✅ Set direction (RTL/LTR)
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);

    this.translate.onLangChange.subscribe((event) => {
      const dir = event.lang === 'ar' ? 'rtl' : 'ltr';

      document.documentElement.setAttribute('dir', dir);
      document.documentElement.setAttribute('lang', event.lang);
      document.body.setAttribute('dir', dir);
      // ✅ send language to backend
      this.userService.updateLanguage(event.lang).subscribe({
        next: () => {
          console.log('Language updated successfully');
        },
        error: (err) => {
          console.error('Language update failed', err);
        },
      });
    });

    // ✅ Initialize dark mode service
    this.darkModeService.listenToSystemThemeChanges();

    this.platform.ready().then(async () => {
      await this.initApp();
      console.log('🔥 PLATFORM READY');

      if (Capacitor.isNativePlatform()) {
        Keyboard.addListener('keyboardWillShow', () => {
          document.body.classList.add('keyboard-open');
        });
        Keyboard.addListener('keyboardWillHide', () => {
          document.body.classList.remove('keyboard-open');
        });
        console.log('🔥 CALLING SocialLogin.initialize');

        try {
          await SocialLogin.initialize(nativeSocialLoginConfig());
          console.log('✅ SocialLogin initialized');
        } catch (e) {
          console.error('❌ SocialLogin init failed', e);
        }
      }

      this.setupBackButtonHandler();
      this.setupBrowserOverlayHistoryHandler();
    });

    this.loadUserInfo();
    this.loadUserRoles();

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        const currentPath = this.getRoutePath(event.urlAfterRedirects);
        const hiddenRoutes = [
          '/login',
          '/register',
          '/capture-id',
          '/selfie-driver',
          '/finish-account',
          '/verify-email',
          '/verify-otp',
          '/verify-register-email',
          '/onboarding-notifications',
          '/forgot-password',
          '/reset-password',
        ];
        // Hide auth chrome even when the route has query params.
        const hideChrome = hiddenRoutes.includes(currentPath);
        this.showTabBar = !hideChrome;
        this.showHeader = !hideChrome;
      }
    });
  }

  private loadUserRoles(): void {
    const roles = localStorage.getItem('roles'); // roles stored as JSON array
    if (roles) {
      try {
        const parsedRoles: string[] = JSON.parse(roles);
        this.permissionsService.loadPermissions(parsedRoles); // tell NgxPermissionsService
        this.userRoles = parsedRoles; // optional, keep a local copy
        console.log('Loaded roles:', parsedRoles);
      } catch (err) {
        console.error('Failed to parse roles from localStorage:', err);
      }
    }
  }
  async ngOnInit() {
    console.log('[App] ngOnInit start');
    this.escapeListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('Simulating hardware back');
        this.handleBackButton({ canGoBack: window.history.length > 1 });
      }
    };

    this.userService.getUser().subscribe({
      next: (user: any) => {
        console.log('Logged user:', user);
        if (user) this.authService.setUser(user);
      },
      error: () => {
        console.log('Not logged in');
      },
    });

    window.addEventListener('keydown', this.escapeListener);
    const isNative = Capacitor.isNativePlatform();
    console.log('[App] Running as native app?', isNative);
    void this.appUpdateService.check();

    // -----------------------------
    // 1) NATIVE DEEP LINKS
    // -----------------------------
    if (isNative) {
      // A flag to track if we've explicitly handled the app's initial state
      let initialStateHandled = false;
      // StatusBar: move final styling to `initApp()` (after SplashScreen.hide())
      // because the splash screen / theme can override the status bar color.
      // We still ensure plugin availability here but defer the actual color/style
      // change until after the app has finished initialising.
      try {
        // Ensure StatusBar plugin is available
        const info = await StatusBar.getInfo?.();
        console.debug('[StatusBar] available info:', info);
      } catch (err) {
        console.warn('StatusBar plugin info check failed', err);
      }
      // Listener for when a deep link opens the app (cold start or already running).
      // Capacitor fires this outside Angular's zone — wrap so the UI actually updates.
      App.addListener('appUrlOpen', (data: any) => {
        console.log('DEEPLINK EVENT FIRED', data);
        this.deepLinkOpened = true;
        initialStateHandled = true;
        console.log(
          '[App] appUrlOpen fired (NATIVE), deepLinkOpened = true',
          data?.url,
        );

        if (!data?.url) return;

        this.ngZone.run(() => {
          void this.openAppLink(data.url);
        });
      });

      // Cold start: also apply getLaunchUrl so we don't depend only on appUrlOpen timing.
      App.getLaunchUrl().then((data) => {
        console.log('Launch URL result:', data);

        if (!data || !data.url) {
          console.log(
            '[App] No native deep link at launch → running auto-login',
          );
          this.loadActiveUser();
        } else {
          this.deepLinkOpened = true;
          console.log('[App] Native deep link at launch → opening', data.url);
          this.ngZone.run(() => {
            void this.openAppLink(data.url);
          });
        }
        initialStateHandled = true;
      });

      // Fallback: In some older versions or specific OS situations, getLaunchUrl might be delayed.
      // If after a short time we haven't handled the state, load the user.
      // Note: The `getLaunchUrl` is generally more reliable, but a tiny delay ensures *something* runs.
      setTimeout(() => {
        if (!initialStateHandled) {
          console.warn(
            '[App] Fallback: Initial state not handled by listener or getLaunchUrl, running auto-login.',
          );
          this.loadActiveUser();
        }
      }, 500); // 500ms for safety, but `getLaunchUrl` should be nearly instant.
    }

    // -----------------------------
    // 2) BROWSER DEEP LINKS
    // -----------------------------
    if (!isNative) {
      const browserUrl = window.location.href;
      console.log('[App] Browser URL:', browserUrl);

      const handled = this.handleBrowserDeepLink(browserUrl);

      if (!handled) {
        console.log('[App] No browser deep link → running auto-login');
        this.loadActiveUser();
      } else {
        console.log('[App] Browser deep link detected → skip auto-login');
      }
    }

    // -----------------------------
    // 3) STORAGE LISTENER
    // -----------------------------
    window.addEventListener('storage', (event) => {
      if (event.key === 'user') {
        console.log('[App] activeUser changed in another tab');
        this.loadActiveUser();
        this.pushService.getNotificationsFromDatabase();
      }
    });

    // -----------------------------
    // 4) APP STATE CHANGE LISTENER
    // -----------------------------
    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        console.log('[App] App resumed; fetch notifications');
        this.pushService.getNotificationsFromDatabase();
        void this.appUpdateService.check();
      }
    });

    // -----------------------------
    // 5) SERVICE WORKER MESSAGES (browser)
    // -----------------------------
    const userId = this.authService.getUserId();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'notification-click') {
          console.log('[App] SW notification click', event.data);
          this.pushService.handleNotificationAction(event.data.data, userId);
        }
      });
    }

    console.log('[App] ngOnInit end');
  }

  /* -------------------------------------------------
   Helper function for browser deep links
------------------------------------------------- */
  handleBrowserDeepLink(url: string): boolean {
    const link = this.appLink.parse(url);
    if (!link || link.path === '/' || link.path === '/login') {
      return false;
    }

    console.log('[App] Browser deep link detected:', link.path);
    this.deepLinkOpened = true;
    void this.openAppLink(url);
    return true;
  }

  private lastOpenedAppUrl: string | null = null;
  private lastOpenedAppUrlAt = 0;

  private async openAppLink(rawUrl: string): Promise<void> {
    if (!rawUrl) return;

    const now = Date.now();
    if (
      rawUrl === this.lastOpenedAppUrl &&
      now - this.lastOpenedAppUrlAt < 800
    ) {
      return;
    }
    this.lastOpenedAppUrl = rawUrl;
    this.lastOpenedAppUrlAt = now;

    const link = this.appLink.parse(rawUrl);
    if (!link) {
      console.error('[App] invalid deep link url', rawUrl);
      return;
    }

    this.hydrateSessionFromStorage();

    if (this.appLink.isApkFileUrl(rawUrl)) {
      const opened = await openUrlInAndroidChrome(rawUrl);
      if (!opened) {
        const toast = await this.toastCtrl.create({
          message: this.translate.instant('ADMIN.BUILDS.DOWNLOAD_FAILED'),
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
      return;
    }

    if (this.appLink.isApkListingUrl(rawUrl)) {
      if (!this.permissionsService.getPermission('Admin')) {
        if (!localStorage.getItem('auth_token')) {
          await this.router.navigate(['/login'], {
            queryParams: { redirectTo: '/admin?tab=builds' },
            replaceUrl: true,
          });
          return;
        }
        const toast = await this.toastCtrl.create({
          message: this.translate.instant('app.doesnotHavePermissionAccess'),
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
        return;
      }
      this.activeTab = 'admin';
    }

    this.appLink.opened$.next(link);

    try {
      await this.router.navigate([link.path], {
        queryParams: link.queryParams,
        replaceUrl: true,
        onSameUrlNavigation: 'reload',
      });
    } catch (err) {
      console.error('[App] failed to open deep link', err);
    }
  }

  private hydrateSessionFromStorage(): void {
    const activeUser = localStorage.getItem('user');
    const authToken = localStorage.getItem('auth_token');
    if (activeUser && authToken) {
      try {
        this.currentUser = JSON.parse(activeUser);
      } catch {
        this.currentUser = null;
      }
    }
    this.loadUserRoles();
  }

  async loadActiveUser() {
    const activeUser = localStorage.getItem('user');
    const authToken = localStorage.getItem('auth_token');
    const currentPath = this.getRoutePath(this.router.url);

    if (activeUser && authToken) {
      this.currentUser = JSON.parse(activeUser);
      // ...

      // ✅ Navigate to main page if no deep link and currently on / or /login
      // Note: On native, `this.deepLinkOpened` is set by the listener.
      // The `getLaunchUrl` call is what decides if `loadActiveUser` is even called.
      // If it *is* called, we assume no deep link has taken precedence.

      if (
        !this.deepLinkOpened &&
        (currentPath === '/' || currentPath === '/login')
      ) {
        const restoreRoute =
          currentPath === '/' ? null : this.getRestorableActiveRoute();
        console.log(
          '[App] restoring active user route:',
          restoreRoute || '/home',
        );
        await this.router.navigateByUrl(restoreRoute || '/home', {
          replaceUrl: true,
        });
      }
    } else if (activeUser && !authToken) {
      console.warn(
        '[App] found stale active user without auth token; clearing stale session and routing to /login',
      );
      this.clearStaleSession();

      if (!this.deepLinkOpened) {
        if (this.isPublicAuthRoute(currentPath)) {
          console.log('[App] public auth route active — keeping current route');
          return;
        }

        console.log('[App] stale session detected — navigating to /login');
        await this.router.navigate(['/login'], { replaceUrl: true });
      }
    } else {
      this.currentUser = null;
      if (!this.deepLinkOpened) {
        const resumeRoute = this.getPendingAuthResumeRoute();

        if (resumeRoute && (currentPath === '/' || currentPath === '/login')) {
          console.log('[App] resuming pending auth flow:', resumeRoute);
          await this.router.navigateByUrl(resumeRoute, { replaceUrl: true });
          return;
        }

        if (this.isPublicAuthRoute(currentPath)) {
          console.log('[App] public auth route active — skipping /login nav');
          return;
        }

        console.log(
          '[App] no active user and no deep link — navigating to /login',
        );
        await this.router.navigate(['/login'], { replaceUrl: true });
      } else {
        console.log(
          '[App] no active user but deep link present — skipping /login nav',
        );
      }
    }
  }

  private clearStaleSession(): void {
    this.pushService.clearLocalNotifications();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('roles');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    this.currentUser = null;
  }

  private getRoutePath(url: string): string {
    return (url || '/').split(/[?#]/)[0] || '/';
  }

  private isProfileRoute(url: string): boolean {
    return this.getRoutePath(url).startsWith('/profile');
  }

  private isPublicAuthRoute(path: string): boolean {
    return this.publicAuthRoutes.includes(this.getRoutePath(path));
  }

  private rememberActiveRoute(url: string): void {
    const routePath = this.getRoutePath(url);

    if (routePath === '/' || this.isPublicAuthRoute(routePath)) {
      return;
    }

    localStorage.setItem(this.lastActiveRouteKey, url);
  }

  private getRestorableActiveRoute(): string | null {
    const route = localStorage.getItem(this.lastActiveRouteKey);

    if (!route) {
      return null;
    }

    const routePath = this.getRoutePath(route);

    if (routePath === '/' || this.isPublicAuthRoute(routePath)) {
      localStorage.removeItem(this.lastActiveRouteKey);
      return null;
    }

    return route;
  }

  private getPendingAuthResumeRoute(): string | null {
    if (localStorage.getItem('registrationRedirectingToLogin') === 'true') {
      localStorage.removeItem('authResumeRoute');
      localStorage.removeItem('pendingRegistrationVerification');
      localStorage.removeItem('registrationRedirectingToLogin');
      return null;
    }

    const route = localStorage.getItem('authResumeRoute');

    if (!route) {
      return null;
    }

    const routePath = this.getRoutePath(route);
    if (
      routePath === '/register' &&
      !localStorage.getItem('pendingRegistrationVerification')
    ) {
      localStorage.removeItem('authResumeRoute');
      return null;
    }

    return this.isPublicAuthRoute(routePath) ? route : null;
  }

  async goToProfile() {
    this.activeTab = 'profile';
    const path = this.getRoutePath(this.router.url);
    if (path === '/profile') {
      return;
    }
    if (path.startsWith('/profile/')) {
      await this.navCtrl.navigateBack('/profile');
      return;
    }
    await this.router.navigate(['/profile'], { replaceUrl: true });
  }

  async AddRide() {
    this.activeTab = 'home';

    await this.navCtrl.navigateRoot(['/add-offered-trip'], { replaceUrl: true });
  }

  async goToFindRide() {
    this.activeTab = 'home';
    await this.navCtrl.navigateRoot(['/home'], { replaceUrl: true });
  }

  async goToHome() {
    this.activeTab = 'home';
    await this.navCtrl.navigateRoot(['/home'], { replaceUrl: true });
  }

  async goToRides() {
    this.activeTab = 'rides';
    const path = this.getRoutePath(this.router.url);
    if (path === '/rides') {
      return;
    }
    await this.navCtrl.navigateRoot(['/rides'], { replaceUrl: true });
  }

  // Example of dynamically showing the Admin button based on permissions

  async goToAdmin() {
    this.activeTab = 'admin';
    const hasAdminPermission = this.permissionsService.getPermission('Admin'); // synchronous check
    console.log('Permission check result:', hasAdminPermission);

    if (hasAdminPermission) {
      const path = this.router.url.split('?')[0];
      if (path === '/admin') {
        return;
      }
      await this.router.navigate(['/admin'], { replaceUrl: true });
    } else {
      const toast = await this.toastCtrl.create({
        message: this.translate.instant('app.doesnotHavePermissionAccess'),
        duration: 3000,
        position: 'top',
        color: 'danger',
      });
      await toast.present();
    }
  }
  async initApp() {
    // Set native system bars after startup so the splash/theme does not override them.
    try {
      await this.darkModeService.updateSystemBarsStyle();
      console.debug('[SystemBars] updated for current theme');
    } catch (err) {
      console.warn('System bars update failed', err);
    }

    const userId = this.authService.getUserId();
    await this.pushService.initPush(userId);

    if (userId) {
      // ✅ Fetch unread notifications on app start
      this.pushService.getNotificationsFromDatabase();
    } else {
      //await this.pushService.initPush(0);
    }
  }

  loadUserInfo() {
    const name = localStorage.getItem('userName');
    const email = localStorage.getItem('userEmail');
    const roles = localStorage.getItem('roles'); // Parse the roles stored as a string
    if (name) this.userName = name;
    if (email) this.userEmail = email;
    console.log('rolesss', roles);
    // If roles exist and it's a valid JSON string, parse it into an array
    if (roles) {
      try {
        this.userRoles = JSON.parse(roles); // Parse the roles string into an array
        console.log('this.userRoles', this.userRoles);
      } catch (error) {
        console.error('Error parsing roles:', error);
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateContainerHeight();
  }

  @HostListener('window:popstate')
  async onBrowserPopState(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    if (this.suppressNextOverlayPopstate) {
      this.suppressNextOverlayPopstate = false;
      return;
    }

    this.dismissingOverlayFromHistory = true;
    const dismissedOverlay = await this.dismissTopOverlay();
    this.dismissingOverlayFromHistory = false;

    if (dismissedOverlay) {
      this.overlayHistoryDepth = Math.max(0, this.overlayHistoryDepth - 1);
    }
  }

  private updateContainerHeight(): void {
    this.viewportHeight = window.innerHeight;
  }

  async goToNotifications() {
    this.activeTab = 'notifications';
    await this.navCtrl.navigateRoot(['/notifications'], { replaceUrl: true });
  }

  private lastHandledBack = 0;
  private lastBackPress = 0;
  private lastProfileChildBack = 0;
  private BACK_PRESS_INTERVAL = 2000; // 2 seconds

  private async setupBackButtonHandler() {
    const onBack = async () => {
      await this.handleBackButton({ canGoBack: window.history.length > 1 });
    };

    this.platform.backButton.subscribeWithPriority(100, onBack);
    void App.addListener('backButton', onBack);
  }

  private setupBrowserOverlayHistoryHandler(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const overlayPresented = () => {
      if (this.dismissingOverlayFromHistory) {
        return;
      }

      window.history.pushState(
        {
          ...(window.history.state ?? {}),
          appOverlay: true,
        },
        '',
        window.location.href,
      );
      this.overlayHistoryDepth += 1;
    };

    const overlayDismissed = () => {
      if (this.dismissingOverlayFromHistory || this.overlayHistoryDepth === 0) {
        return;
      }

      this.overlayHistoryDepth = Math.max(0, this.overlayHistoryDepth - 1);
      this.suppressNextOverlayPopstate = true;
      window.history.back();
    };

    document.addEventListener('ionModalDidPresent', overlayPresented);
    document.addEventListener('ionPopoverDidPresent', overlayPresented);
    document.addEventListener('ionActionSheetDidPresent', overlayPresented);
    document.addEventListener('ionAlertDidPresent', overlayPresented);

    document.addEventListener('ionModalDidDismiss', overlayDismissed);
    document.addEventListener('ionPopoverDidDismiss', overlayDismissed);
    document.addEventListener('ionActionSheetDidDismiss', overlayDismissed);
    document.addEventListener('ionAlertDidDismiss', overlayDismissed);
  }

  private async dismissTopOverlay(): Promise<boolean> {
    const modal = await this.modalCtrl.getTop();
    if (modal) {
      await modal.dismiss();
      console.log('[BACK] Modal dismissed');
      return true;
    }

    const actionSheet = await this.actionSheetCtrl.getTop();
    if (actionSheet) {
      await actionSheet.dismiss();
      console.log('[BACK] ActionSheet dismissed');
      return true;
    }

    const alert = await this.alertCtrl.getTop();
    if (alert) {
      await alert.dismiss();
      console.log('[BACK] Alert dismissed');
      return true;
    }

    const popctrl = await this.popoverCtrl.getTop();
    if (popctrl) {
      await popctrl.dismiss();
      console.log('[BACK] Popover dismissed');
      return true;
    }

    return false;
  }

  async handleBackButton({ canGoBack }: any) {
    const now = Date.now();
    if (now - this.lastHandledBack < 400) {
      return;
    }
    this.lastHandledBack = now;

    console.log('[BACK] Back button pressed');

    if (this.appUpdateService.forceUpdate) {
      return;
    }

    // 1️⃣ Overlay ALWAYS wins
    if (await this.dismissTopOverlay()) {
      return;
    }

    // 4️⃣ INPUT (important for phone input)
    const activeEl = document.activeElement as HTMLElement;
    if (
      activeEl &&
      (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')
    ) {
      activeEl.blur();
      console.log('[BACK] Input blurred');
      return;
    }

    const current = this.getRoutePath(this.router.url);
    const forceHomePages = [
      '/add-offered-trip',
      '/find-ride',
      '/admin',
      '/notifications',
      '/rides',
    ];
    const exitPages = ['/login', '/home'];
    const blockedPages = [
      '/register',
      '/capture-id',
      '/verify-email',
      '/verify-otp',
    ];

    // --------- Handle /profile child pages ----------
    if (current.startsWith('/profile/') && current !== '/profile') {
      console.log('[BACK] Navigating from child profile page to /profile');
      this.activeTab = 'profile';
      this.lastProfileChildBack = now;
      await this.navCtrl.navigateBack('/profile');
      return;
    }

    // --------- Handle exact /profile ----------
    if (current === '/profile') {
      if (now - this.lastProfileChildBack < 800) {
        return;
      }
      this.activeTab = 'home';
      await this.router.navigate(['/home'], { replaceUrl: true });
      return;
    }

    // --------- Force home for other pages ----------
    if (forceHomePages.includes(current)) {
      console.log('[BACK] Force navigation to /home from: ' + current);
      this.activeTab = 'home';
      await this.router.navigate(['/home'], { replaceUrl: true });
      return;
    }

    // --------- Double-back exit pages ----------
    if (exitPages.includes(current)) {
      if (now - this.lastBackPress < this.BACK_PRESS_INTERVAL) {
        console.log('[BACK] Exiting app');
        App.exitApp();
        return;
      }
      this.lastBackPress = now;
      this.showExitToast(this.translate.instant('app.PressAgainToexist'));
      return;
    }

    // --------- Blocked pages ----------
    if (blockedPages.includes(current)) {
      console.log('[BACK] Back blocked on:', current);
      return;
    }

    // --------- Normal navigation ----------
    if (canGoBack) {
      console.log('[BACK] Normal window.history.back()');
      window.history.back();
      return;
    }

    // --------- Fallback (exit app) ----------
    if (now - this.lastBackPress < this.BACK_PRESS_INTERVAL) {
      App.exitApp();
      return;
    }
    this.lastBackPress = now;
    this.showExitToast(this.translate.instant('app.PressAgainToexist'));
  }

  async showExitToast(
    message: string = this.translate.instant('app.PressAgainToexist'),
  ) {
    const toast = await this.toastCtrl.create({
      message,
      duration: this.BACK_PRESS_INTERVAL,
      position: 'top',
    });
    await toast.present();
  }
  onSegmentClick(value: string) {
    this.activeTab = value;

    switch (value) {
      case 'home':
        this.goToHome();
        break;
      case 'rides':
        this.goToRides();
        break;
      case 'admin':
        this.goToAdmin();
        break;
      case 'profile':
        this.goToProfile();
        break;
      case 'notifications':
        this.goToNotifications();
        break;
    }
  }
}
